import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { MemberDTO } from '@wd/shared';
import { errorText } from '../../lib/api';
import { todayCN } from '../../lib/clock';
import { useCreateProject, useMe, useMembers, useProjectActions } from '../../lib/queries';
import { Icon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { Avatar, Button, Modal } from '../../ui/primitives';
import './projectForm.css';

// 新建项目 / 编辑项目基本信息（名称、负责人、团队、参与人）共用一个弹窗。
// 权限以服务端为准：普通成员只能新建自己或直属下属负责的项目。

export interface EditableProject {
  id: string;
  code: string;
  name: string;
  ownerId: string;
  teamId: string | null;
  participantIds: string[];
}

const eligible = (m: MemberDTO) => m.status === 'active' || m.status === 'invited';

export function ProjectFormModal({
  project,
  defaultTeamId,
  onClose,
  onCreated,
}: {
  /** 传了就是编辑，不传就是新建 */
  project?: EditableProject;
  defaultTeamId?: string | null;
  onClose: () => void;
  /** 新建成功后的处理；不传则打开新项目 */
  onCreated?: (r: { id: string; code: string }) => void;
}) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: members = [] } = useMembers();
  const create = useCreateProject();
  const { editBasics } = useProjectActions(project?.id);
  const editing = !!project;

  const [name, setName] = useState(project?.name ?? '');
  const [ownerId, setOwnerId] = useState(project?.ownerId ?? me?.id ?? '');
  const [teamId, setTeamId] = useState<string | null>(project ? project.teamId : (defaultTeamId ?? me?.teamId ?? null));
  const [teamTouched, setTeamTouched] = useState(editing || !!defaultTeamId);
  const [people, setPeople] = useState<Set<string>>(() => new Set(project?.participantIds.filter((id) => id !== project.ownerId) ?? []));
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const addBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!ownerId && me) {
      setOwnerId(me.id);
      if (!teamTouched) setTeamId(me.teamId ?? null);
    }
  }, [me, ownerId, teamTouched]);

  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  // 新建时普通成员只能选自己和直属下属当负责人；主管、管理员和编辑时可以选任何在岗的人
  const owners = useMemo(() => {
    const list = members.filter(eligible);
    if (editing || me?.role === 'admin' || me?.role === 'manager') return list;
    return list.filter((m) => m.id === me?.id || m.managerId === me?.id);
  }, [members, me, editing]);
  const candidates = members.filter((m) => eligible(m) && m.id !== ownerId);

  const pickOwner = (id: string) => {
    setOwnerId(id);
    setPeople((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
    // 没手动选过团队时，团队跟着负责人走
    if (!teamTouched) setTeamId(byId.get(id)?.teamId ?? null);
  };
  const togglePerson = (id: string) =>
    setPeople((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const busy = create.isPending || editBasics.isPending;
  const save = async () => {
    setError(null);
    try {
      if (project) {
        const before = new Set(project.participantIds.filter((id) => id !== project.ownerId));
        await editBasics.mutateAsync({
          name,
          ownerId,
          teamId,
          add: [...people].filter((id) => !before.has(id)),
          // 原负责人换成别人后留在参与人里，除非这里把他去掉
          remove: [...before].filter((id) => !people.has(id) && id !== ownerId),
        });
        onClose();
      } else {
        const r = await create.mutateAsync({ name, ownerId, teamId, participantIds: [...people], dueDate: dueDate || null });
        if (onCreated) onCreated(r);
        else navigate(`/projects/${r.id}`);
        onClose();
      }
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <Modal
      width={520}
      title={editing ? '编辑项目' : '新建项目'}
      subtitle={project?.code}
      onClose={onClose}
      footNote={editing ? '移出的人从明天起不再预填这个项目' : '编号自动生成；负责人和参与人的日报会按这个项目预填'}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={!name.trim() || !ownerId || busy} onClick={() => void save()}>
            {editing ? '保存' : '新建项目'}
          </Button>
        </>
      }
    >
      <div className="pf-form">
        <label>
          <span className="k">名称</span>
          <input className="wd-input" value={name} maxLength={40} autoFocus onChange={(e) => setName(e.target.value)} placeholder="例如：德国 EMS 认证" />
          {name !== '' && !name.trim() && <span className="t-meta c-red">项目名称不能只有空格</span>}
        </label>
        <div className="pf-row">
          <label>
            <span className="k">负责人</span>
            <select className="pf-select" value={ownerId} onChange={(e) => pickOwner(e.target.value)}>
              {owners.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.teamName ? ` · ${m.teamName}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="k">团队</span>
            <select
              className="pf-select"
              value={teamId ?? ''}
              onChange={(e) => {
                setTeamId(e.target.value || null);
                setTeamTouched(true);
              }}
            >
              <option value="">不属于任何团队</option>
              {(me?.teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="pf-field">
          <span className="k">参与人（可选）</span>
          <div className="pf-people">
            {[...people].map((id) => {
              const m = byId.get(id);
              return (
                <button key={id} type="button" className="pf-chip" title="移出" onClick={() => togglePerson(id)}>
                  <Avatar initial={m?.initial ?? '?'} seed={m?.name} size={16} />
                  {m?.name ?? '已离开'}
                  <Icon name="close" size={12} />
                </button>
              );
            })}
            <button ref={addBtn} type="button" className="pf-add" onClick={() => setPicking(true)}>
              <Icon name="plus" size={14} />
              添加
            </button>
          </div>
          <Popover anchor={addBtn} open={picking} onClose={() => setPicking(false)} width={260}>
            <MenuList
              search
              items={candidates.map((m) => ({ value: m.id, label: m.name, icon: <Avatar initial={m.initial} seed={m.name} />, hint: m.teamName ?? '' }))}
              selected={people}
              onPick={togglePerson}
              emptyText="没有可以加入的人"
            />
          </Popover>
        </div>
        {!editing && (
          <label>
            <span className="k">截止日（可选，之后也能在项目里设）</span>
            <input className="wd-input" type="date" value={dueDate} min={todayCN()} onChange={(e) => setDueDate(e.target.value)} />
          </label>
        )}
        {error && <div className="t-meta c-red">{error}</div>}
      </div>
    </Modal>
  );
}
